/**
 * Locale-aware number, currency, and date formatters.
 *
 * Locale is derived from the active i18n language:
 *   'es' → 'es-ES'   (e.g. 1.234,56 €)
 *   'en' → 'en-US'   (e.g. €1,234.56)
 *
 * Currency is determined by the filing country, NOT the UI language:
 *   'ES' → 'EUR'
 *   'US' → 'USD'
 */

function resolveLocale(language: string): string {
  if (language === 'es') return 'es-ES';
  if (language === 'ru') return 'ru-RU';
  if (language === 'ja') return 'ja-JP';
  return 'en-US';
}

/**
 * Format a monetary amount.
 *
 * @param amount   Numeric value to format
 * @param language Active UI language ('en' | 'es')
 * @param currency ISO 4217 currency code derived from filing country ('EUR' | 'USD')
 */
export function formatCurrency(
  amount: number,
  language: string,
  currency: string
): string {
  const locale = resolveLocale(language);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format a plain number with locale-appropriate thousands/decimal separators.
 *
 * @param n        Numeric value
 * @param language Active UI language
 */
export function formatNumber(n: number, language: string): string {
  const locale = resolveLocale(language);
  return new Intl.NumberFormat(locale).format(n);
}

/**
 * Format a Date (or ISO-string) for display.
 *
 * @param date     Date object or ISO-8601 string
 * @param language Active UI language
 */
export function formatDate(
  date: Date | string,
  language: string
): string {
  const locale = resolveLocale(language);
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}